import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
// @ts-ignore
import t from "../../../helpers/t";
import "./UiModal.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import UiButton from "../UiButton/UiButton";

export type ModalButton = { text: React.ReactNode } & Omit<
  React.ComponentProps<typeof UiButton>,
  "children"
>;

type Props = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode; // Optional Header-Slot for the title
  footer?: React.ReactNode; // Optional Footer-Slot for buttons or other content
  showCloseButton?: boolean; // Close button in the top-right corner (Default: true)
  closeOnOverlayClick?: boolean; // Clicking on the overlay closes the modal (Default: true)
  closeOnEscape?: boolean; // Pressing Escape closes the modal (Default: true)
  size?: "sm" | "md" | "lg" | "xl"; // Size of the modal (Default: "md")
  maxWidth?: number | string; // Override the max-width of the modal (md = 30rem = 480px)
  width?: number | string;
  padding?: number | string;
  noPadding?: boolean;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  // Replace the default modal body with a custom React element.
  // Overlay, centering, Escape and overlay click remain active.
  customBody?: React.ReactNode;
  buttons?: ModalButton[]; // Buttons rendered in their own footer above the regular `footer`
  children?: React.ReactNode;
};

let modalCounter = 0;

/**
 * @description
 * Universal modal component
 *
 * @example
 * <UiModal open={open} onClose={() => setOpen(false)} title="Titel">
 *   Inhalt
 * </UiModal>
 *
 * <UiModal
 * open={open}
 * onClose={() => setOpen(false)}
 * title="Löschen?"
 * buttons={[
 *   { text: "Abbrechen", outline: true, onClick: () => setOpen(false) },
 *   { text: "Löschen", color: "#d33", onClick: handleDelete },
 * ]}
 * >
 *   Inhalt
 * </UiModal>
 *
 */
export default function UiModal({
  open,
  onClose,
  title,
  footer,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  size = "md",
  maxWidth,
  width,
  padding,
  noPadding = false,
  className,
  style,
  ariaLabel,
  customBody,
  buttons,
  children,
}: Props) {
  const [titleId] = useState(() => `inb-ui-modal-title-${++modalCounter}`);

  // Body-Scroll lock when modal is open
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Escape closes the modal
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="inb-ui-modal__overlay" onClick={closeOnOverlayClick ? onClose : undefined}>
      <div
        className={[
          "inb-ui-modal__wrap",
          customBody === undefined ? `inb-ui-modal__wrap--${size}` : "inb-ui-modal__wrap--custom",
        ].join(" ")}
        style={{
          ...(customBody === undefined && maxWidth !== undefined && { maxWidth }),
          ...(customBody === undefined && width !== undefined && { width }),
        }}
      >
        {customBody !== undefined ? (
          <div className="inb-ui-modal__custom-body" onClick={(event) => event.stopPropagation()}>
            {customBody}
          </div>
        ) : (
          <>
            {showCloseButton && (
              <button
                type="button"
                className="inb-ui-modal__close"
                onClick={(event) => {
                  event.stopPropagation();
                  onClose();
                }}
                aria-label={t("close")}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? titleId : undefined}
              aria-label={!title ? ariaLabel : undefined}
              className={["inb-ui-modal", noPadding && "inb-ui-modal--no-padding", className]
                .filter(Boolean)
                .join(" ")}
              style={{
                ...(padding !== undefined && { padding }),
                ...style, // Own style overrides all other styles
              }}
              onClick={(event) => event.stopPropagation()}
            >
              {title && (
                <header className="inb-ui-modal__header">
                  <h2 id={titleId} className="inb-ui-modal__title">
                    {title}
                  </h2>
                </header>
              )}
              <div className="inb-ui-modal__content">{children}</div>
              {buttons && buttons.length > 0 && (
                <footer className="inb-ui-modal__buttons">
                  {buttons.map(({ text, ...buttonProps }, index) => (
                    <UiButton key={index} {...buttonProps}>
                      {text}
                    </UiButton>
                  ))}
                </footer>
              )}
              {footer && <footer className="inb-ui-modal__footer">{footer}</footer>}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
